import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { unwrapCitationParens, type NumberedLink } from '../utils/markdownLinks';
import { paragraphize } from '../utils/paragraphize';
import { useIsDownloadRoute } from '../utils/useIsDownloadRoute';
import { useCitationNumbering } from './CitationNumbering';
import { InlineCitation } from './InlineCitation';
import { InlineCitationStatic } from './InlineCitationStatic';

interface AnalysisMarkdownProps {
  text?: string;
  className?: string;
}

const BLOCK_COMPONENTS: Components = {
  p: ({ children }) => <p style={{ margin: '0 0 0.25em 0' }}>{children}</p>,
  ul: ({ children }) => (
    <ul style={{ margin: '4px 0', paddingLeft: '1.25em', listStyleType: 'disc' }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '4px 0', paddingLeft: '1.25em', listStyleType: 'decimal' }}>{children}</ol>
  ),
  h1: ({ children }) => <strong>{children}</strong>,
  h2: ({ children }) => <strong>{children}</strong>,
  h3: ({ children }) => <strong>{children}</strong>,
  h4: ({ children }) => <strong>{children}</strong>,
  h5: ({ children }) => <strong>{children}</strong>,
  h6: ({ children }) => <strong>{children}</strong>,
};

/**
 * A citation renders where its link sits in the prose, so the anchor's own text is dropped
 * and replaced by the number the URL was assigned. An href with no number is one
 * `numberLinks` rejected (a non-http(s) scheme, or a malformed URL), which keeps its label
 * as plain text rather than pointing somewhere we would not open.
 */
const buildLinkRenderer = (byUrl: Map<string, NumberedLink>, exporting: boolean): Components['a'] =>
  function CitationAnchor({ href, children }) {
    const link = href ? byUrl.get(href) : undefined;
    if (!link) return <>{children}</>;
    return exporting ? <InlineCitationStatic link={link} /> : <InlineCitation link={link} />;
  };

const AnalysisMarkdownInner = ({ text, className }: AnalysisMarkdownProps) => {
  const exporting = useIsDownloadRoute();

  const sanitized = useMemo(
    // eslint-disable-next-line no-control-regex
    () => (text ? text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '') : ''),
    [text],
  );
  const unwrapped = useMemo(() => unwrapCitationParens(sanitized), [sanitized]);
  const formatted = useMemo(() => paragraphize(unwrapped), [unwrapped]);
  // Numbers come from the surrounding provider when there is one, so they continue across
  // the page's blocks; a block on its own numbers from 1.
  const { register } = useCitationNumbering();
  const links = register(formatted);
  // `register` returns a fresh array per render, so the numbers themselves are the identity
  // to memoise against — rebuilding the renderer only when a URL's number actually changes.
  const numbering = links.map((link) => `${link.number}:${link.url}`).join();
  const components = useMemo(
    () => ({
      ...BLOCK_COMPONENTS,
      a: buildLinkRenderer(new Map(links.map((link) => [link.url, link])), exporting),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `numbering` stands in for `links`, whose array identity changes every render
    [numbering, exporting],
  );

  if (!text) return null;

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {formatted}
      </ReactMarkdown>
    </div>
  );
};

export const AnalysisMarkdown = memo(AnalysisMarkdownInner);
